import React, { Component, ChangeEvent } from 'react'
import { css, cx } from '@emotion/css'
import { CsSearchIcon, CsCloseIcon } from '../icons'
import { theme as deprecatedTheme } from '../../styled/theme'
import { theme } from '@contrast-security-inc/design-system-foundations'
import {
  injectIntl,
  WrappedComponentProps,
  MessageDescriptor
} from 'react-intl'

// testing hooks
export const iconTestID = 'search-text-icon-test-id'
export const inputTestID = 'search-text-input-test-id'
export const closeIconTestID = 'search-text-close-icon-test-id'

const outerCss = css`
  padding-bottom: ${deprecatedTheme.space[1]}px;
  border-bottom: 1px solid ${theme.color.neutral60};
  transition: border-color 0.2s;
  flex: none;
  display: flex;
  align-items: center;
  width: 274px;
  font-size: 14px;
`

const outerCollapsedCss = cx(
  outerCss,
  css`
    border-bottom: 1px solid transparent;
  `
)

const inputCellCss = css`
  overflow: hidden;
  flex: 1 0 auto;
  transition: width 0.2s;
`

const inputCellCollapsedCss = cx(
  inputCellCss,
  css`
    width: 0;
    flex: none;
  `
)

const searchIconCellCss = css`
  width: 24px;
  flex: none;
  cursor: pointer;
`

const searchIconCellWhenValueCss = cx(
  searchIconCellCss,
  css`
    cursor: default;
  `
)

const searchIconCss = css`
  height: 16px;
  width: 16px;
  color: ${theme.color.neutral90};
`

// blocks click events on the icon when the input is expanded
// so that only the blur event gets handled
const searchIconExpandedCss = cx(
  searchIconCss,
  css`
    pointer-events: none;
  `
)

const inputCss = css`
  outline: none;
  border: none;
  background: transparent;
  padding: 0;
  width: 100%;

  ::placeholder {
    color: ${theme.color.neutral50};
    opacity: 1;
    transition: opacity 0.4s;
  }

  ::-ms-clear {
    display: none;
  }
`

const inputCssWithValue = cx(
  inputCss,
  css`
    ::placeholder {
      color: ${theme.color.neutral50};
      opacity: 0;
    }
  `
)

const closeIconCellCss = css`
  display: flex;
  justify-content: flex-end;
  width: 20px;
  flex: none;
  opacity: 1;
  transition: opacity 0.4s;
  overflow: hidden;
`

const closeIconCellCollapsedCss = cx(
  closeIconCellCss,
  css`
    width: 0;
    opacity: 0;
    transition: opacity 0.4s, width 0s 0.4s;
  `
)

const closeIconCss = css`
  height: 12px;
  width: 12px;
  color: ${theme.color.neutral40};
`

interface Props {
  readonly defaultValue?: string
  readonly placeholderI18n: MessageDescriptor
  readonly onChange: (value: string) => void
  readonly onFocus?: () => void
  readonly className?: string
  readonly searchIconClassName?: string
  readonly alwaysShowPlaceholder?: boolean
}

interface State {
  readonly expandInput: boolean
  readonly value: string
}

interface SearchInputProps {
  readonly placeholderI18n: MessageDescriptor
  readonly value: string
  readonly handleInputChange: (e: ChangeEvent<HTMLInputElement>) => void
  readonly handleInputFocus?: () => void
  readonly handleInputBlur: () => void
  readonly getRef: (ref: HTMLInputElement | null) => void
}
class SearchInput extends React.Component<
  SearchInputProps & WrappedComponentProps,
  {}
> {
  public render() {
    const {
      getRef,
      value,
      handleInputChange,
      handleInputFocus,
      handleInputBlur,
      placeholderI18n,
      intl
    } = this.props
    return (
      <input
        ref={getRef}
        type="text"
        value={value}
        onChange={handleInputChange}
        onFocus={handleInputFocus}
        onBlur={handleInputBlur}
        placeholder={intl.formatMessage(placeholderI18n)}
        className={value ? inputCssWithValue : inputCss}
        data-testid={inputTestID}
      />
    )
  }
}

const I18nSearchInput = injectIntl(SearchInput, {
  forwardRef: true
})

class SearchTextInput extends Component<Props, State> {
  public readonly state: State

  private inputRef: HTMLInputElement | undefined

  constructor(props) {
    super(props)

    const { defaultValue } = props
    const value = defaultValue ? defaultValue : ''

    this.state = {
      value,
      expandInput: !!value
    }
  }

  public render() {
    const {
      className,
      searchIconClassName,
      placeholderI18n,
      alwaysShowPlaceholder,
      onFocus
    } = this.props
    const { expandInput, value } = this.state

    return (
      <div
        className={
          expandInput
            ? cx(outerCss, className)
            : cx(outerCollapsedCss, className)
        }
      >
        <div
          className={!!value ? searchIconCellWhenValueCss : searchIconCellCss}
        >
          <CsSearchIcon
            className={
              expandInput
                ? cx(searchIconExpandedCss, searchIconClassName)
                : cx(searchIconCss, searchIconClassName)
            }
            onClick={this.handleSearchIconClick}
            data-testid={iconTestID}
          />
        </div>
        <div
          className={
            alwaysShowPlaceholder || expandInput
              ? inputCellCss
              : inputCellCollapsedCss
          }
        >
          <I18nSearchInput
            value={value}
            placeholderI18n={placeholderI18n}
            handleInputChange={this.handleInputChange}
            handleInputBlur={this.handleInputBlur}
            handleInputFocus={onFocus}
            getRef={(ref) => (this.inputRef = ref ? ref : undefined)}
          />
        </div>
        <div
          className={
            expandInput && value ? closeIconCellCss : closeIconCellCollapsedCss
          }
          data-testid="search-icon-closed-icon-collapsed"
        >
          <CsCloseIcon
            className={closeIconCss}
            onClick={this.handleCloseIconClick}
            data-testid={closeIconTestID}
          />
        </div>
      </div>
    )
  }

  private handleInputBlur = () => {
    this.setState({ expandInput: !!this.state.value })
  }

  private handleSearchIconClick = () => {
    this.setState(
      (prevState) => {
        return {
          ...prevState,
          expandInput: !prevState.expandInput
        }
      },
      () => {
        if (this.state.expandInput && this.inputRef) {
          this.inputRef.focus()
        }
      }
    )
  }

  private handleCloseIconClick = () => {
    this.setInputValue('')
    if (this.inputRef) {
      this.inputRef.focus()
    }
  }

  private handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    this.setInputValue(e.target.value)
  }

  private setInputValue = (value: string) => {
    this.setState({ value, expandInput: !!value })
    this.props.onChange(value)
  }
}

export { SearchTextInput }
